import { useAuth } from '../../context/AuthContext';
import { useEffect, useState, type FormEvent } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { GET_USER_PROFILE } from '../../graphql';
import { loadProfilePhoto, uploadProfilePhoto } from '../../lib/profilePhoto';
import { initialsFor } from '../../lib/initials';
import { Loader } from '../common/PageLoader';
import { PasswordInput } from '../common/PasswordInput';
import { RESEND_SECONDS } from '../account/TinycmsLogin';
import { AvatarImage } from '../common/Avatar';
import { UsageConsentSetting } from '../common/UsageTracking';
import { showInkToast } from '../cms/InkToast';
import { askInk } from '../cms/InkConfirm';

const REQUEST_PASSWORD_CHANGE_CODE = gql`
  mutation RequestPasswordChangeCode {
    requestPasswordChangeCode
  }
`;

const CHANGE_PASSWORD = gql`
  mutation ChangePassword($code: String!, $newPassword: String!) {
    changePassword(code: $code, newPassword: $newPassword)
  }
`;

interface UserProfileData {
  me?: {
    id: string;
    email: string;
    name?: string | null;
    photoUrl?: string | null;
  };
}

// The name and photo live on the person's users row (apiserver
// platform/services/profile.js), so every browser and site shows the same.
const SIGN_OUT_EVERYWHERE = gql`
  mutation SignOutEverywhere {
    signOutEverywhere
  }
`;

const SESSION_KEPT = gql`
  query SessionKept {
    sessionKept
  }
`;

const UPDATE_PROFILE = gql`
  mutation UpdateProfile($name: String, $photoUrl: String) {
    updateProfile(name: $name, photoUrl: $photoUrl) {
      id
      email
      name
      photoUrl
    }
  }
`;

// Checked here only: the server receives a hash, never the password itself.
const MIN_PASSWORD = 8;
const PASSWORD_TOO_SHORT = `Use at least ${MIN_PASSWORD} characters.`;

// Saved per person, for every site they're on (apiserver
// platform/services/notificationPrefs.js); a kind's emails check it first.
const NOTIFICATION_PREFS = gql`
  query NotificationPrefs {
    notificationPrefs {
      ask
      digest
      fail
      available
    }
  }
`;

const SET_NOTIFICATION_PREF = gql`
  mutation SetNotificationPref($kind: String!, $on: Boolean!) {
    setNotificationPref(kind: $kind, on: $on) {
      ask
      digest
      fail
      available
    }
  }
`;

type NotificationKind = 'ask' | 'digest' | 'fail';
type NotificationPrefs = Record<NotificationKind, boolean> & { available: boolean };

const NOTIFICATIONS = [
  ['ask', 'An agent asks for approval', 'Email and in-app, as it happens'],
  ['digest', 'Daily digest', 'What agents published and changed, every morning at 8 AM'],
  ['fail', 'A publish is blocked', 'When checks stop a scheduled publish'],
] as const;

export function Profile() {
  const { logout } = useAuth();
  const { loading, error, data } = useQuery<UserProfileData>(GET_USER_PROFILE);
  const email = data?.me?.email || '';
  const userId = data?.me?.id || '';
  const savedName = data?.me?.name || '';
  const savedPhotoUrl = data?.me?.photoUrl || '';
  const [name, setName] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [saveProfile] = useMutation<{ updateProfile: NonNullable<UserProfileData['me']> }>(
    UPDATE_PROFILE
  );
  const [photoError, setPhotoError] = useState('');
  const [uploading, setUploading] = useState(false);
  const { data: prefsData } = useQuery<{ notificationPrefs: NotificationPrefs }>(
    NOTIFICATION_PREFS
  );
  const [saveNotif] = useMutation<{ setNotificationPref: NotificationPrefs }>(
    SET_NOTIFICATION_PREF,
    {
      update(cache, { data: res }) {
        if (res)
          cache.writeQuery({
            query: NOTIFICATION_PREFS,
            data: { notificationPrefs: res.setNotificationPref },
          });
      },
    }
  );
  // The switch flips at once; the saved answer replaces it.
  const [pendingNotifs, setPendingNotifs] = useState<Partial<Record<NotificationKind, boolean>>>(
    {}
  );
  const saved = prefsData?.notificationPrefs;
  const notifs = {
    ask: saved?.ask ?? true,
    digest: saved?.digest ?? true,
    fail: saved?.fail ?? true,
    ...pendingNotifs,
  };
  const toggleNotif = async (kind: NotificationKind, label: string) => {
    if (pendingNotifs[kind] !== undefined) return;
    const on = !notifs[kind];
    setPendingNotifs((p) => ({ ...p, [kind]: on }));
    try {
      await saveNotif({ variables: { kind, on } });
      showInkToast(`${label}: ${on ? 'on' : 'off'}.`);
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t save. Try again.');
    } finally {
      setPendingNotifs((p) => {
        const next = { ...p };
        delete next[kind];
        return next;
      });
    }
  };

  const [changingPassword, setChangingPassword] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordOk, setPasswordOk] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [hashing, setHashing] = useState(false);

  const [requestCode, { loading: requesting }] = useMutation(REQUEST_PASSWORD_CHANGE_CODE);
  const [changePassword, { loading: changing }] = useMutation(CHANGE_PASSWORD);
  const [signOutEverywhere] = useMutation<{ signOutEverywhere: boolean }>(SIGN_OUT_EVERYWHERE);
  // Asked apart from the profile: a server without this field only loses the words.
  const { data: sessionData } = useQuery<{ sessionKept: boolean }>(SESSION_KEPT, {
    fetchPolicy: 'network-only',
  });
  const endAllSessions = async () => {
    const ok = await askInk({
      title: 'Sign out of all devices?',
      body: 'Every browser signed in to your account has to sign in again, this one too. Agents connected in AI Agents aren’t affected.',
      confirmLabel: 'Sign out everywhere',
      run: {
        busyLabel: 'Signing out…',
        failure:
          'We couldn’t sign out your other devices. Nothing changed, so you’re still signed in everywhere. Try again.',
        retryLabel: 'Try again',
        work: async () => {
          const { data: done } = await signOutEverywhere();
          if (!done?.signOutEverywhere) throw new Error('not signed out');
        },
      },
    });
    if (!ok) return;
    showInkToast('Signed out of all devices.');
    logout('You have been signed out of all devices.');
  };

  useEffect(() => {
    setName(savedName);
  }, [savedName]);

  // The photo on the profile; a photo this browser kept before photos were
  // stored on the server (2026-10-27) is moved there once, then forgotten.
  useEffect(() => {
    if (!userId) return;
    if (savedPhotoUrl) {
      setPhotoUrl(savedPhotoUrl);
      return;
    }
    const kept = loadProfilePhoto(userId);
    setPhotoUrl(kept);
    if (!kept) return;
    void saveProfile({ variables: { photoUrl: kept } })
      .then(() => localStorage.removeItem(`tinycms.profilePhoto.${userId}`))
      .catch(() => undefined);
  }, [userId, savedPhotoUrl, saveProfile]);

  // Saved when the field is left (or Enter), only when it changed.
  const commitName = async () => {
    const next = name.replace(/\s+/g, ' ').trim();
    if (next === savedName) {
      setName(next);
      return;
    }
    try {
      const r = await saveProfile({ variables: { name: next || null } });
      setName(r.data?.updateProfile.name || '');
      showInkToast(next ? 'Name saved' : 'Name cleared');
    } catch (err) {
      setName(savedName);
      showInkToast(message(err, 'Could not save the name.'));
    }
  };

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const initials = initialsFor(name, email);

  const message = (err: unknown, fallback: string) =>
    err instanceof Error && err.message ? err.message : fallback;

  const openPasswordChange = () => {
    setChangingPassword(true);
    setCodeSent(false);
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordOk('');
  };

  const cancelPasswordChange = () => {
    setChangingPassword(false);
    setCodeSent(false);
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const sendCode = async () => {
    setPasswordError('');
    setPasswordOk('');
    try {
      await requestCode();
      setCodeSent(true);
      setCode('');
      setCooldown(RESEND_SECONDS);
      setPasswordOk('A 6-digit code is on its way. It works for 10 minutes.');
    } catch (err) {
      setPasswordError(message(err, 'The code couldn’t be sent. Try again.'));
    }
  };

  const submitPasswordChange = async (e: FormEvent) => {
    e.preventDefault();
    if (code.replace(/\D/g, '').length !== 6) {
      setPasswordError('Enter the 6-digit code from the email.');
      return;
    }
    if (newPassword.length < MIN_PASSWORD) {
      setPasswordError(PASSWORD_TOO_SHORT);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Those passwords don’t match.');
      return;
    }
    setPasswordError('');
    setPasswordOk('');
    setHashing(true);
    try {
      await changePassword({
        variables: {
          code,
          newPassword,
        },
      });
      setChangingPassword(false);
      setCodeSent(false);
      setCode('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordOk('Password updated.');
    } catch (err) {
      setPasswordError(message(err, 'Could not change the password. Try again.'));
    } finally {
      setHashing(false);
    }
  };

  if (loading) return <Loader label="Loading" className="ink-lede" />;
  if (error) return <p className="ink-lede">Could not load your profile.</p>;

  const busy = requesting || changing || hashing;

  return (
    // Its sections rise in one after another (ink.css [data-enter-blocks]).
    <div data-enter-blocks="">
      <h1 className="ink-h1">Profile settings</h1>
      <p className="ink-lede">Your sign-in and how tinycms reaches you.</p>

      <div className="ink-profile-photo">
        <span className="ink-profile-avatar">
          <AvatarImage url={photoUrl} initials={initials} />
        </span>
        <label className="ink-bill-link" style={{ cursor: 'pointer' }}>
          Change photo
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            hidden
            disabled={uploading || !userId}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file || !userId) return;
              const preview = URL.createObjectURL(file);
              setPhotoUrl(preview);
              setPhotoError('');
              setUploading(true);
              void uploadProfilePhoto(file)
                .then((url) => saveProfile({ variables: { photoUrl: url } }))
                .then((r) => setPhotoUrl(r.data?.updateProfile.photoUrl || ''))
                .catch((err: unknown) => {
                  setPhotoUrl(savedPhotoUrl);
                  setPhotoError(err instanceof Error ? err.message : 'Could not save that photo.');
                })
                .finally(() => {
                  URL.revokeObjectURL(preview);
                  setUploading(false);
                });
            }}
          />
        </label>
        {uploading ? <span className="ink-team-email">Saving photo…</span> : null}
        {photoError ? <span className="ink-team-email">{photoError}</span> : null}
      </div>

      <div className="ink-profile-grid">
        <label>
          Name
          <input
            className="ink-acct-input"
            value={name}
            placeholder="Your name"
            maxLength={80}
            autoComplete="name"
            onChange={(e) => setName(e.target.value)}
            onBlur={() => void commitName()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
          />
        </label>
        <label>
          Email
          <input
            className="ink-acct-input"
            type="email"
            value={email}
            placeholder="Email"
            readOnly
          />
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <span style={{ font: '500 13px/1.3 var(--ink-font)' }}>Password</span>
          {!changingPassword ? (
            <button
              type="button"
              className="ink-btn"
              style={{ width: 'max-content' }}
              onClick={openPasswordChange}
            >
              Change password
            </button>
          ) : (
            <form
              onSubmit={submitPasswordChange}
              style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 360 }}
            >
              <p className="ink-team-email" style={{ margin: 0 }}>
                We’ll email a code to {email}. Enter it below to unlock the new password.
              </p>
              {!codeSent ? (
                <button
                  type="button"
                  className="ink-btn ink-btn-primary"
                  style={{ width: 'max-content' }}
                  disabled={busy}
                  onClick={() => void sendCode()}
                >
                  {requesting ? 'Sending…' : 'Email me a code'}
                </button>
              ) : (
                <>
                  <label>
                    Code
                    <input
                      className="ink-acct-input"
                      value={code}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="XXXXXX"
                      autoFocus
                      onChange={(e) => {
                        // Digits only, the first six: a pasted "196 391" still works.
                        setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                        setPasswordError('');
                      }}
                    />
                  </label>
                  <label>
                    New password
                    <PasswordInput
                      className="ink-acct-input"
                      value={newPassword}
                      placeholder="New password"
                      aria-describedby="profile-new-password-help"
                      autoComplete="new-password"
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setPasswordError('');
                      }}
                    />
                    <span
                      id="profile-new-password-help"
                      className="ink-team-email"
                      style={
                        passwordError === PASSWORD_TOO_SHORT
                          ? { color: 'var(--ink-danger, #b42318)' }
                          : undefined
                      }
                    >
                      At least {MIN_PASSWORD} characters
                    </span>
                  </label>
                  <label>
                    Confirm password
                    <PasswordInput
                      className="ink-acct-input"
                      value={confirmPassword}
                      placeholder="Confirm password"
                      autoComplete="new-password"
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setPasswordError('');
                      }}
                    />
                  </label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button type="submit" className="ink-btn ink-btn-primary" disabled={busy}>
                      {busy ? 'Saving…' : 'Update password'}
                    </button>
                    <button
                      type="button"
                      className="ink-btn ink-btn-quiet"
                      disabled={busy || cooldown > 0}
                      onClick={() => void sendCode()}
                    >
                      {cooldown > 0 ? `Send again in ${cooldown}s` : 'Send a new code'}
                    </button>
                  </div>
                </>
              )}
              <button
                type="button"
                className="ink-bill-link"
                style={{ width: 'max-content', background: 'none', border: 0, padding: 0 }}
                disabled={busy}
                onClick={cancelPasswordChange}
              >
                Cancel
              </button>
            </form>
          )}
          {passwordError ? (
            <span className="ink-team-email" style={{ color: 'var(--ink-danger, #b42318)' }}>
              {passwordError}
            </span>
          ) : null}
          {passwordOk && !passwordError ? (
            <span className="ink-team-email">{passwordOk}</span>
          ) : null}
        </div>
      </div>

      <h2 className="ink-bill-h">Privacy</h2>
      <div className="ink-profile-notifs">
        <UsageConsentSetting />
      </div>

      <h2 className="ink-bill-h">Notifications</h2>
      <p className="ink-bill-sub" style={{ marginTop: 4 }}>
        How tinycms reaches you. Click a row to change it.
      </p>
      <div className="ink-profile-notifs">
        {NOTIFICATIONS.map(([key, label, desc]) => (
          <button
            key={key}
            type="button"
            className="ink-profile-notif"
            role="switch"
            aria-checked={notifs[key]}
            onClick={() => void toggleNotif(key, label)}
          >
            <span style={{ flex: 1 }}>
              <span className="ink-profile-notif-name">{label}</span>
              <span className="ink-profile-notif-desc">{desc}</span>
            </span>
            <span className="ink-profile-switch" data-on={notifs[key] ? 'true' : 'false'} />
          </button>
        ))}
      </div>

      {/* Sign out lives here now, not in the Account rail (2026-10-24). */}
      <div className="ink-profile-signout">
        <button
          type="button"
          className="ink-btn"
          onClick={() => logout('You have been successfully logged out.')}
        >
          Sign out
        </button>
        <span className="ink-team-email">
          Signed in as {email || 'you'} on this browser
          {sessionData?.sessionKept ? ', kept signed in for 30 days after you last use it' : ''}.
        </span>
        <button type="button" className="ink-quiet-link" onClick={() => void endAllSessions()}>
          Sign out of all devices
        </button>
      </div>
    </div>
  );
}

/**
 * Profile settings (UX optimization review, 2026-10-24): its own page, from
 * the avatar menu — photo, name, email, password, privacy, notifications and
 * Sign out. It used to be the Profile tab inside Account.
 */
export function ProfilePage() {
  return (
    <div className="ink-page ink-page-flow">
      <div className="ink-account ink-account-solo">
        <div className="ink-account-main">
          <Profile />
        </div>
      </div>
    </div>
  );
}
